import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import MarketShell from '../components/market/MarketShell';
import { marketImageUrl } from '../lib/marketCatalog';
import { formatSar } from '../lib/marketPricing';
import { customerStep, listMyOrders, MARKET_API_IS_LOCAL, stepLabel } from '../lib/marketOrdersApi';
import PreviewBanner from '../components/market/PreviewBanner';
import { StatusBadge } from './MarketOrderDetail';

// HioBuy phase 3 — the buyer's market orders. RLS returns only their own.

const T = {
  ar: { title: 'طلباتي', empty: 'لا توجد طلبات بعد.', browse: 'تصفّح المنتجات', items: (n) => `${n} بند`, preview: 'بيئة اختبار.' },
  en: { title: 'My orders', empty: 'No orders yet.', browse: 'Browse products', items: (n) => `${n} item${n === 1 ? '' : 's'}`, preview: 'Test environment.' },
  zh: { title: '我的订单', empty: '暂无订单。', browse: '浏览商品', items: (n) => `${n} 项`, preview: '测试环境。' },
};

export default function MarketOrders({ lang = 'ar', user }) {
  const nav = useNavigate();
  const isAr = lang === 'ar';
  const c = T[lang] || T.ar;
  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
  usePageTitle('market', lang);
  const [rows, setRows] = useState(null);
  useEffect(() => { listMyOrders().then((r) => setRows(r.rows)); }, []);
  const date = (iso) => new Date(iso).toLocaleDateString(isAr ? 'ar-SA' : lang === 'zh' ? 'zh-CN' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <MarketShell lang={lang} user={user}>
      <div className="fx-wrap">
        {MARKET_API_IS_LOCAL && <PreviewBanner text={c.preview} isAr={isAr} />}
        <h1 className={`fx-h1${isAr ? ' ar' : ''}`} style={{ fontSize: isAr ? 24 : 27, margin: '6px 0 18px' }}>{c.title}</h1>
        {!rows ? <p style={{ color: 'var(--text-secondary)' }}>…</p> : !rows.length ? (
          <div style={{ padding: 30, textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 16, fontFamily: font }}>
            <p style={{ color: 'var(--text-secondary)' }}>{c.empty}</p>
            <button type="button" onClick={() => nav('/market')} style={{ padding: '9px 18px', borderRadius: 12, border: '1px solid var(--border-strong)', background: 'var(--surface-raised)', cursor: 'pointer', fontFamily: font }}>{c.browse}</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map((o) => {
              const first = o.market_order_lines?.[0];
              return (
                <button type="button" key={o.id} data-order-row={o.order_no} onClick={() => nav(`/market/orders/${o.id}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, borderRadius: 16, border: '1px solid var(--border)', background: 'var(--surface-raised)', cursor: 'pointer', textAlign: isAr ? 'right' : 'left', fontFamily: font }}>
                  {first?.image_ref && <img src={marketImageUrl(first.image_ref, true)} alt="" style={{ width: 54, height: 54, borderRadius: 10, objectFit: 'cover' }} />}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }} dir="ltr">{o.order_no}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(isAr ? first?.title_ar : lang === 'zh' ? first?.title_zh : first?.title_en) || first?.title_en || ''}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{date(o.created_at)} · {c.items(o.market_order_lines?.length ?? 0)}</span>
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: isAr ? 'flex-start' : 'flex-end', gap: 6 }}>
                    <strong>{formatSar(Number(o.total_sar))}</strong>
                    <StatusBadge status={customerStep(o.status)} label={stepLabel(customerStep(o.status), lang)} />
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </MarketShell>
  );
}
